import { forwardRef, ReactNode } from 'react'

import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useRouter } from '@tribeplatform/react-sdk'
import { IconButton } from '@tribeplatform/react-ui-kit/Button'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { PopoverTourStep } from '../Onboarding/AdminModeOnboarding/PopoverTourStep.js'

import { Breadcrumbs } from './BreadCrumbs.js'
import { VerticalDivider } from './Divider.js'
import { AnchorComponentProps } from './modes/index.js'
import { ModeSwitcher } from './ModeSwitcher.js'
import { TrialBadge } from './TrialBadge.js'
import { useDarkThemeStyles } from './useDarkThemeStyles.js'
import { useVisitSiteAnchor } from './useVisitSiteAnchor.js'
import { Workspaces } from './Workspaces.js'

export const TOP_NAVBAR_ID = 'admin-top-navbar'

/**
 * Height of the admin top navigation bar. Single source of truth so anything
 * that needs to clear the bar (e.g. the trial-expired overlay) stays in sync.
 */
export const TOP_NAVBAR_HEIGHT = '3rem'

const DefaultVisitSiteLink = forwardRef<
  HTMLAnchorElement,
  AnchorComponentProps
>((props, ref) => {
  const { Link } = useRouter()
  return <Link ref={ref} href="/" target="_blank" {...props} />
})
DefaultVisitSiteLink.displayName = 'DefaultVisitSiteLink'

export function TopNavBar({ menu }: { menu?: ReactNode }) {
  const { $t } = useI18n()
  const VisitSiteComp = useVisitSiteAnchor() ?? DefaultVisitSiteLink
  /**
   * To always apply dark theme styles to the navbar as the navbar is always dark regardless of the network theme.
   */
  const navBarTheme = useDarkThemeStyles({ elementId: TOP_NAVBAR_ID })

  return (
    <>
      {/* eslint-disable-next-line react/no-danger */}
      <style dangerouslySetInnerHTML={{ __html: navBarTheme }} />
      <div
        id={TOP_NAVBAR_ID}
        style={{ height: TOP_NAVBAR_HEIGHT }}
        className="flex bg-surface border-line-subdued border-b overflow-hidden justify-between"
      >
        <div className="flex items-center px-4 sm:px-0">
          {menu}
          <PopoverTourStep
            step={1}
            title={
              <T
                id="AdminModeOnboarding.PopoverTour.Workspaces.Title"
                defaultMessage="Access all your {COMMUNITIES}"
              />
            }
            description={
              <T
                id="AdminModeOnboarding.PopoverTour.Workspaces.Description"
                defaultMessage="Use this menu to jump between {COMMUNITIES}."
              />
            }
          >
            <Workspaces />
          </PopoverTourStep>
          <VerticalDivider className="hidden lg:block" />
          <div className="px-2 flex-1 hidden lg:block">
            <Breadcrumbs />
          </div>
        </div>
        <div className="flex items-center">
          <TrialBadge />
          <VerticalDivider />
          <ModeSwitcher />
          <VerticalDivider />
          <PopoverTourStep
            step={2}
            title={
              <T
                id="AdminModeOnboarding.PopoverTour.VisitSite.Title"
                defaultMessage="Preview the live experience"
              />
            }
            description={
              <T
                id="AdminModeOnboarding.PopoverTour.VisitSite.Description"
                defaultMessage="Open your {COMMUNITY} in a new tab to see it the way members do."
              />
            }
          >
            <div className="px-2 flex items-center">
              <IconButton
                as={VisitSiteComp}
                variant="plainNeutral"
                icon={<SvgIcon name="globe" />}
                tooltip
                className="h-auto"
                accessibilityLabel={$t({
                  defaultMessage: 'Visit Site',
                  id: 'Generics.VisitSite',
                })}
              />
            </div>
          </PopoverTourStep>
        </div>
      </div>
    </>
  )
}
